import * as XLSX from 'xlsx';
import type { SimulationRunSummary } from '../../../../types/simulation';

type StatsRow = {
  Metric: string;
  Value: string | number;
  Unit: string;
};

/**
 * Large or nested payloads merged onto the run summary are not metrics from
 * GET /results/{id}/metrics and must not be embedded in the stats workbook.
 */
const STAT_EXPORT_EXCLUDED_KEYS = new Set<string>([
  'ride_points',
  'activities',
  'unmatched_rides',
  'metrics_fetched',
  'frodoReport',
  'frodoOffloadedRides',
]);

interface KnownStatField {
  label: string;
  field: keyof SimulationRunSummary;
  unit?: string;
}

/**
 * Curated labels/units for known summary fields. Order is stable for spreadsheets;
 * any additional keys returned by the API are appended afterward (sorted).
 */
const KNOWN_STAT_FIELDS: KnownStatField[] = [
  { label: 'Results ID', field: 'results_id' },
  { label: 'Run ID', field: 'run_id' },
  { label: 'Scenario Name', field: 'scenario_name' },
  { label: 'Run Time', field: 'run_time' },
  { label: 'Role', field: 'role' },
  { label: 'Success Ratio', field: 'success_ratio', unit: '%' },
  { label: 'Total Requests', field: 'no_rides' },
  { label: 'Number of Requests', field: 'num_requests' },
  { label: 'Utilization', field: 'passenger_per_vehicle_hour' },
  { label: 'Max Groups', field: 'max_pax' },
  { label: 'Available Vehicles', field: 'no_vehicles' },
  { label: 'Allocated Vehicles', field: 'allocated_vehicle_shifts' },
  { label: 'Vehicle Hours', field: 'vehicle_hours', unit: 'h' },
  { label: 'Shared Trip Rate', field: 'pcnt_shared_rides', unit: '%' },
  { label: 'Vehicle Distance', field: 'total_km_travelled', unit: 'km' },
  { label: 'First 25% Wait Time', field: 'waiting_time_min_first_quartile', unit: 'min' },
  { label: 'Median Wait Time', field: 'waiting_time_min_median', unit: 'min' },
  { label: 'Third Quartile Wait Time', field: 'waiting_time_min_third_quartile', unit: 'min' },
  { label: 'Minimum Wait Time', field: 'waiting_time_min_minimum', unit: 'min' },
  { label: 'Maximum Wait Time', field: 'waiting_time_min_maximum', unit: 'min' },
  { label: 'Average Wait Time', field: 'waiting_time_min_avg', unit: 'min' },
  { label: 'Active Distance', field: 'active_km', unit: 'km' },
  { label: 'Idle Distance', field: 'dead_running_km', unit: 'km' },
  { label: 'Median Trip Distance', field: 'trip_distance_median', unit: 'km' },
  { label: 'Minimum Trip Distance', field: 'trip_distance_min', unit: 'km' },
  { label: 'First Quartile Trip Distance', field: 'trip_distance_q1', unit: 'km' },
  { label: 'Third Quartile Trip Distance', field: 'trip_distance_q3', unit: 'km' },
  { label: 'Maximum Trip Distance', field: 'trip_distance_max', unit: 'km' },
  { label: 'Average Trip Distance', field: 'trip_distance_average', unit: 'km' },
  { label: 'Median Trip Time', field: 'median_time_on_vehicle', unit: 'min' },
  { label: 'Minimum Trip Time', field: 'min_time_on_vehicle', unit: 'min' },
  { label: 'Maximum Trip Time', field: 'max_time_on_vehicle', unit: 'min' },
  { label: 'Average Trip Time', field: 'average_time_on_vehicle', unit: 'min' },
  { label: 'FRODO Enabled', field: 'frodoEnabled' },
];

function toDisplayValue(value: unknown, isPercent: boolean): string | number {
  if (value == null || value === '') return '--';
  if (typeof value === 'boolean') return value ? 'true' : 'false';
  if (typeof value === 'number' && Number.isFinite(value) && isPercent) {
    return Number((value <= 1 ? value * 100 : value).toFixed(2));
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    return Number(value.toFixed(4));
  }
  return String(value);
}

function formatUnknownStatValue(value: unknown): string | number {
  if (value == null || value === '') return '--';
  if (typeof value === 'boolean') return value ? 'true' : 'false';
  if (typeof value === 'number' && Number.isFinite(value)) {
    return Number(value.toFixed(4));
  }
  if (typeof value === 'string' || typeof value === 'bigint') return String(value);
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}

function humanizeKey(key: string): string {
  if (!key) return key;
  const withSpaces = key
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/_/g, ' ')
    .trim();
  return withSpaces
    .split(/\s+/)
    .map((w) => (w.length === 0 ? w : w.charAt(0).toUpperCase() + w.slice(1)))
    .join(' ');
}

/**
 * Accepts the merged run summary (metrics + list fields). Cast to a record so
 * extra keys returned by GET /results/{id}/metrics still appear in the export.
 */
export function downloadSimulationStatsXLSX(summary: Partial<SimulationRunSummary>, runId?: string | null): void {
  const record = summary as Record<string, unknown>;
  const usedKeys = new Set<string>();

  const rows: StatsRow[] = KNOWN_STAT_FIELDS.map(({ label, field, unit }) => {
    const key = field as string;
    usedKeys.add(key);
    const isPercent = unit === '%';
    return {
      Metric: label,
      Value: toDisplayValue(summary[field], isPercent),
      Unit: unit ?? '',
    };
  });

  const extraKeys = Object.keys(record)
    .filter((key) => !STAT_EXPORT_EXCLUDED_KEYS.has(key) && !usedKeys.has(key))
    .sort((a, b) => a.localeCompare(b));

  for (const key of extraKeys) {
    usedKeys.add(key);
    rows.push({
      Metric: humanizeKey(key),
      Value: formatUnknownStatValue(record[key]),
      Unit: '',
    });
  }

  const workbook = XLSX.utils.book_new();
  const sheet = XLSX.utils.json_to_sheet(rows);
  XLSX.utils.book_append_sheet(workbook, sheet, 'Simulation Stats');
  const filename = runId ? `simulation_stats_${runId}.xlsx` : 'simulation_stats.xlsx';
  XLSX.writeFile(workbook, filename);
}
